/**
 * VS: both fighters, the strength label (bots count as an even match), the skill-group bars, the
 * XP line and a short countdown; "Fight now" (or Enter) starts at once.
 * Landscape phones: designs/Caged VS Landscape (2a). Portrait: Caged Home's VS. Desktop: Caged
 * Home Desktop's VS.
 */
import { FIGHT, PROGRESSION, SKILL_GROUPS, type SkillRanks } from '@caged/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MatchFighter } from '../../game/session';
import { useApp } from '../../state/store';
import { ArenaBackdrop } from '../components/Backdrop';
import { FighterArt } from '../components/FighterArt';
import { StrengthChip } from '../components/StrengthChip';
import { recordLabel } from '../format';
import { useFinePointer, useKey, useLayoutMode, useShortLandscape, type LayoutMode } from '../hooks';
import { Flag } from '../league';
import { currentProgress, groupStrength, nextFightXp } from '../progression';
import '../ui.css';
import s from './VsScreen.module.css';

/** Seconds on the VS countdown before the fight starts by itself. */
export const VS_COUNTDOWN_SECONDS = 3;
/** The bot readies up after this long (ms). */
const BOT_READY_MS = 700;
/** How long "GO" shows before the fight (ms). */
const GO_HOLD_MS = 450;

const SHADE =
  'linear-gradient(90deg,rgba(245,160,60,.22),transparent 40%,transparent 60%,rgba(127,214,106,.16)),linear-gradient(180deg,rgba(20,19,18,.8),rgba(20,19,18,.3) 40%,rgba(20,19,18,.95))';

function SkillBars({ me, opp }: { me: SkillRanks; opp: SkillRanks }) {
  const { t } = useTranslation();
  const a = groupStrength(me);
  const b = groupStrength(opp);
  const w = (v: number) => `${Math.max(4, Math.min(96, v))}%`;
  return (
    <div className={s.skills} aria-label={t('screens.vs.skillsLabel')}>
      {SKILL_GROUPS.map((g) => (
        <div key={g} className={s.skillRow}>
          <div className={[s.skillTrack, s.skillTrackMe].join(' ')}>
            <div className={s.skillMe} style={{ width: w(a[g]) }} />
          </div>
          <div className={s.skillName}>{t(`screens.groups.${g}`)}</div>
          <div className={s.skillTrack}>
            <div className={s.skillOpp} style={{ width: w(b[g]) }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function FighterBlock({
  f,
  side,
  line,
  height,
  bot = false,
  plan = false,
}: {
  f: MatchFighter;
  side: 'me' | 'opp';
  line: string;
  height: number;
  /** A bot plays this fighter (shows the "Bot" tag). */
  bot?: boolean;
  /** An absent player's game plan fights for them (a league fight): "Game plan" instead. */
  plan?: boolean;
}) {
  const { t } = useTranslation();
  // The flag sits on the outer side of each name, as the design mirrors them.
  const flag = f.country ? <Flag country={f.country} className={s.flag} /> : null;
  return (
    <div className={s.fighter}>
      <FighterArt id={f.fighterId} height={height} flip={side === 'opp'} delay={side === 'opp' ? 0.7 : 0} alt={f.name} look={f.look} />
      <div className={s.name}>
        {side === 'me' && flag}
        <span className={s.nameText}>{f.name}</span>
        {side === 'opp' && flag}
        {bot && <span className={s.botTag}>{plan ? t('screens.vs.plan') : t('screens.vs.bot')}</span>}
      </div>
      <div className={side === 'me' ? s.lineMe : s.lineOpp}>{line}</div>
    </div>
  );
}

export function VsScreen() {
  const { t } = useTranslation();
  const mode: LayoutMode = useLayoutMode();
  const short = useShortLandscape();
  const fine = useFinePointer();
  const match = useApp((st) => st.match);
  const progress = useApp((st) => st.progress);
  const go = useApp((st) => st.go);
  const [cd, setCd] = useState(VS_COUNTDOWN_SECONDS);
  const [meReady, setMeReady] = useState(false);
  const [oppReady, setOppReady] = useState(false);
  const started = cd === 0;

  useEffect(() => {
    if (!match) go('home');
  }, [match, go]);

  // Countdown; both ready skips straight to GO.
  useEffect(() => {
    if (started) {
      const id = setTimeout(() => go('fight'), GO_HOLD_MS);
      return () => clearTimeout(id);
    }
    const id = setTimeout(() => setCd((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(id);
  }, [cd, started, go]);

  useEffect(() => {
    const id = setTimeout(() => setOppReady(true), BOT_READY_MS);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (meReady && oppReady) setCd(0);
  }, [meReady, oppReady]);

  const ready = useCallback(() => setMeReady(true), []);
  useKey('Enter', ready, !started);

  // Online the server sends the opponent's win-XP band (never a rating); bots count as even.
  const vsBot = !match?.online || match.online.info.vsBot;
  const ratingDiff = match?.online?.info.ratingDiff ?? 0;
  // A league fight (M5): league XP, and the opponent may be an absent player's game plan.
  const league = match?.online?.info.league;
  const xp = useMemo(() => nextFightXp(currentProgress(progress), vsBot, ratingDiff, !!league), [progress, vsBot, ratingDiff, league]);

  if (!match) return null;

  const format = t('screens.ui.format', { rounds: FIGHT.rounds, seconds: FIGHT.roundSeconds });
  const meLine = t('screens.vs.meLine', { level: match.player.level ?? 1, record: recordLabel(t, progress.record) });
  const oppLine = t('screens.vs.oppLine', { level: match.opponent.level ?? 1 });
  const notes = [
    xp.rested ? t('screens.vs.restedNote', { factor: PROGRESSION.rested.factor }) : '',
    xp.vsBot ? t('screens.vs.botNote', { pct: Math.round(PROGRESSION.xp.botFactor * 100) }) : '',
  ].filter(Boolean);

  const xpText = (
    <span className={s.xpText}>
      {t('screens.vs.xpWin')} <b className={s.xpWin}>+{xp.win}</b> · {t('screens.vs.xpLoss')} <b className={s.xpLoss}>+{xp.loss}</b>
    </span>
  );

  const countdown = (
    <div className={s.countdown} aria-live="polite">
      <div className={s.cdLabel}>{t('screens.vs.startsIn')}</div>
      <div key={cd} className={s.cdNum}>
        {cd > 0 ? cd : t('screens.vs.go')}
      </div>
    </div>
  );

  const label = started ? t('screens.vs.fightGo') : meReady ? t('screens.vs.waiting', { name: match.opponent.name }) : t('screens.vs.fightNow');
  const waiting = meReady && !started;
  const button = (
    <button
      type="button"
      className={[s.ready, waiting ? s.readyWaiting : ''].join(' ')}
      onClick={ready}
      disabled={started}
    >
      <span>{label}</span>
      <span className={s.readyRight}>
        {fine && !meReady && <span className={s.kbd}>{t('screens.vs.enterKey')}</span>}
        <span className={[s.dot, meReady ? s.dotOn : ''].join(' ')} aria-hidden="true" />
        <span className={[s.dot, oppReady ? s.dotOn : waiting ? s.dotWait : ''].join(' ')} aria-hidden="true" />
        <span className={s.readyCount}>{(meReady ? 1 : 0) + (oppReady ? 1 : 0)}/2</span>
      </span>
    </button>
  );

  const header = (
    <div className={s.topRow}>
      <span>{league ? t('screens.lobby.leagueFight', { day: league.day }) : t('screens.vs.quickFight')}</span>
      <span>{format}</span>
    </div>
  );

  const xpCard = (
    <div className={s.xpCard}>
      {xpText}
      {notes.length > 0 && <span className={[s.xpNote, xp.rested ? s.xpNoteGreen : ''].join(' ')}>{notes.join(' · ')}</span>}
    </div>
  );

  if (mode === 'portrait') {
    return (
      <div className={['cg-ui', s.root].join(' ')}>
        <ArenaBackdrop tone="vs" shade={SHADE} />
        <div className={s.port}>
          {header}
          <div className={s.portFighters}>
            <FighterBlock f={match.player} side="me" line={meLine} height={118} />
            <FighterBlock f={match.opponent} side="opp" line={oppLine} height={118} bot={vsBot} plan={!!league?.plan} />
            <div className={s.portVs} aria-hidden="true">
              VS
            </div>
          </div>
          <div className={s.center}>
            <StrengthChip ratingDiff={vsBot ? 0 : ratingDiff} />
          </div>
          <SkillBars me={match.player.ranks} opp={match.opponent.ranks} />
          {xpCard}
          <div className={s.spacer} />
          <div className={s.portBottom}>
            {countdown}
            {button}
          </div>
        </div>
      </div>
    );
  }

  const land = mode === 'landscape';
  return (
    <div className={['cg-ui', s.root].join(' ')}>
      <ArenaBackdrop tone="vs" shade={SHADE} />
      <div className={land ? s.land : s.desk}>
        {header}
        <div className={s.mid}>
          <FighterBlock f={match.player} side="me" line={meLine} height={land ? (short ? 92 : 150) : 220} />
          <div className={s.midCenter}>
            <div className={s.vsMark} aria-hidden="true">
              VS
            </div>
            <div className={s.center}>
              <StrengthChip ratingDiff={vsBot ? 0 : ratingDiff} />
            </div>
            <SkillBars me={match.player.ranks} opp={match.opponent.ranks} />
            {xpCard}
          </div>
          <FighterBlock f={match.opponent} side="opp" line={oppLine} height={land ? (short ? 92 : 150) : 220} bot={vsBot} plan={!!league?.plan} />
        </div>
        <div className={s.bottom}>
          {countdown}
          {button}
        </div>
      </div>
    </div>
  );
}
